import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import SocialShareBar from "@/components/public/social-share-bar"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

type ComparisonRow = {
  label: string
  retargeting: string
  cold: string
}

const PERFORMANCE_DATA: ComparisonRow[] = [
  {
    label: "Click-through rate (CTR)",
    retargeting: "2-5% (warm audience)",
    cold: "0.5-1.5% (new audience)",
  },
  {
    label: "Cost per click (CPC)",
    retargeting: "£0.30-0.80",
    cold: "£0.50-1.50",
  },
  {
    label: "Cost per enquiry",
    retargeting: "£2-8",
    cold: "£5-15",
  },
  {
    label: "Conversion rate",
    retargeting: "5-12% of clicks",
    cold: "1-3% of clicks",
  },
  {
    label: "Return on ad spend (ROAS)",
    retargeting: "4-10x typical",
    cold: "1.5-3x typical",
  },
  {
    label: "Time to convert",
    retargeting: "1-7 days",
    cold: "14-30+ days",
  },
]

const STRATEGY_DATA: ComparisonRow[] = [
  {
    label: "Pixel / tracking required",
    retargeting: "Yes (Meta Pixel essential)",
    cold: "No (interest and location targeting)",
  },
  {
    label: "Minimum audience size",
    retargeting: "1,000+ website visitors",
    cold: "50,000+ in target area",
  },
  {
    label: "Creative approach",
    retargeting: "Specific, direct, urgency-led",
    cold: "Broad, aspirational, brand-led",
  },
  {
    label: "Recommended budget split",
    retargeting: "30-40% of total ad spend",
    cold: "60-70% of total ad spend",
  },
  {
    label: "Best video length",
    retargeting: "10-20 seconds (quick reminder)",
    cold: "20-45 seconds (full story)",
  },
  {
    label: "Primary objective",
    retargeting: "Conversions, enquiries, viewings",
    cold: "Awareness, reach, traffic",
  },
  {
    label: "Audience lifespan",
    retargeting: "7-30 day window (recency matters)",
    cold: "Ongoing, evergreen targeting",
  },
]

const HERO_STATS = [
  {
    value: "3-5x",
    label: "Higher CTR with retargeting",
    detail:
      "Retargeting ads typically achieve 3 to 5 times the click-through rate of cold audience campaigns, because viewers already recognise your brand.",
  },
  {
    value: "500K+",
    label: "Cold audience reach potential",
    detail:
      "Cold campaigns can reach hundreds of thousands of potential buyers in a target area, filling the top of your marketing funnel.",
  },
  {
    value: "60%",
    label: "Lower cost per enquiry",
    detail:
      "Retargeting campaigns typically deliver enquiries at 40-60% lower cost than cold audience ads, thanks to higher intent.",
  },
  {
    value: "8-12%",
    label: "Retargeting conversion rate",
    detail:
      "Website visitors who see retargeting ads convert at 8-12%, compared to 1-3% for cold audiences seeing the same listing.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "What is the difference between retargeting and cold audience ads?",
    a: "Retargeting ads are shown to people who have already interacted with your agency (visited your website, watched a video, or engaged with a post). Cold audience ads target people who have never encountered your brand, using interest, demographic, and location targeting. Retargeting converts better because the audience already knows you; cold ads build awareness and fill your pipeline.",
  },
  {
    q: "Do I need a Meta Pixel to run retargeting ads for property?",
    a: "Yes. The Meta Pixel (or Conversions API) is essential for retargeting on Facebook and Instagram. It tracks visitors to your website and lets you create custom audiences. Install it on every page of your site, especially listing pages, valuation pages, and contact forms. Without it, you cannot retarget website visitors.",
  },
  {
    q: "How much should I spend on retargeting vs cold audience ads?",
    a: "A good starting split is 60-70% of your budget on cold audience campaigns and 30-40% on retargeting. Cold campaigns need more budget because they reach larger audiences at lower conversion rates. As your pixel data grows and retargeting audiences expand, you can gradually increase the retargeting share. For most agents, a total budget of £300-500 per month delivers meaningful results.",
  },
  {
    q: "How long should I retarget someone after they visit a listing page?",
    a: "The optimal retargeting window for property is 7 to 21 days. The first 7 days see the highest engagement, as the listing is still fresh in the viewer's mind. After 21 days, interest drops significantly. For premium properties with longer sales cycles, you can extend to 30 days. Always exclude people who have already enquired to avoid wasted spend.",
  },
  {
    q: "What type of video works best for retargeting property ads?",
    a: "Short, direct videos of 10 to 20 seconds work best for retargeting. The viewer has already seen the property, so focus on a compelling hook: a price reduction, an open day invitation, a specific feature highlight, or social proof (number of viewings booked). Include a clear call to action such as 'Book a viewing' or 'Still interested?'. QuiteRight can generate these short-form videos automatically.",
  },
  {
    q: "Can I run retargeting ads on TikTok for property?",
    a: "Yes, TikTok offers retargeting through its Pixel and custom audiences. However, TikTok's retargeting capabilities are less mature than Meta's. For most UK estate agents, Meta (Facebook and Instagram) retargeting delivers stronger results due to the larger homeowner audience and more precise tracking. TikTok is better suited to cold awareness campaigns.",
  },
  {
    q: "How many website visitors do I need before retargeting works?",
    a: "You need a minimum of around 1,000 people in your retargeting audience for Meta to deliver ads effectively. For a typical estate agent website receiving 2,000 to 5,000 monthly visitors, you can build a usable retargeting audience within 2 to 4 weeks. Start with cold campaigns to drive traffic, then layer retargeting once your audience is large enough.",
  },
  {
    q: "Should I use different video creative for retargeting and cold audiences?",
    a: "Absolutely. Cold audience videos should introduce your agency and showcase the property with a full story arc, running 20 to 45 seconds. Retargeting videos should be shorter (10 to 20 seconds), more direct, and assume familiarity. Reference the specific listing or area they viewed. Different creative for each stage of the funnel significantly improves performance.",
  },
]

/* ------------------------------------------------------------------ */
/*  Components                                                         */
/* ------------------------------------------------------------------ */

function AudienceBadge({
  name,
  variant,
}: {
  name: string
  variant: "retargeting" | "cold"
}) {
  const classes =
    variant === "retargeting"
      ? "bg-red text-white"
      : "bg-charcoal text-white"

  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${classes}`}
    >
      {name}
    </span>
  )
}

function ComparisonTable({
  rows,
  title,
}: {
  rows: ComparisonRow[]
  title: string
}) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[600px] text-left">
        <thead>
          <tr className="border-b-2 border-stone">
            <th className="pb-3 pr-4 text-xs font-medium uppercase tracking-[0.15em] text-slate">
              {title}
            </th>
            <th className="pb-3 px-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <AudienceBadge name="Retargeting" variant="retargeting" />
            </th>
            <th className="pb-3 pl-4 text-center text-xs font-medium uppercase tracking-[0.15em]">
              <AudienceBadge name="Cold Audience" variant="cold" />
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={`border-b border-stone/50 ${i % 2 === 0 ? "bg-cream/30" : ""}`}
            >
              <td className="py-3.5 pr-4 text-sm font-medium text-charcoal">
                {row.label}
              </td>
              <td className="py-3.5 px-4 text-center text-sm text-slate">
                {row.retargeting}
              </td>
              <td className="py-3.5 pl-4 text-center text-sm text-slate">
                {row.cold}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function FaqAccordion({ items }: { items: FaqItem[] }) {
  return (
    <dl className="divide-y divide-stone">
      {items.map((item, i) => (
        <details key={i} className="group">
          <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
            <dt className="text-sm font-semibold leading-relaxed text-charcoal">
              {item.q}
            </dt>
            <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
              <svg
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                className="size-3"
              >
                <path d="M8 3v10M3 8h10" />
              </svg>
            </span>
          </summary>
          <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
        </details>
      ))}
    </dl>
  )
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function RetargetingVsColdAudienceAds({
  meta,
}: {
  meta: SeoMeta
}) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline:
              "Retargeting vs Cold Audience Video Ads for Property: Which Strategy Wins?",
            description: meta.description,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
              logo: {
                "@type": "ImageObject",
                url: "https://www.quiteright.co.uk/qr-logo-wordmark-red.png",
              },
            },
            datePublished: "2026-03-12",
            dateModified: "2026-03-12",
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema
          canonical={meta.canonical}
          items={[
            { name: "Guides", path: "/guides" },
            { name: "Retargeting vs Cold Audience Property Video Ads" },
          ]}
        />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Advertising strategy 2026
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Retargeting vs cold audience video ads for property
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Two strategies, two audiences, one goal: more enquiries. This guide
            compares retargeting and cold audience video ads for UK estate agents
            who want to get more from every pound of ad spend. Whether you advertise through <a href="https://www.facebook.com/business/ads" target="_blank" rel="noopener noreferrer">Meta Ads Manager</a> or <a href="https://ads.google.com" target="_blank" rel="noopener noreferrer">Google Ads</a>, your audience strategy matters more than your platform choice.
          </p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <AudienceBadge name="Retargeting" variant="retargeting" />
            <AudienceBadge name="Cold Audience" variant="cold" />
          </div>
        </div>
      </section>

      <SocialShareBar />

      {/* Stats */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The numbers
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Why audience strategy matters
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {HERO_STATS.map((stat, i) => (
              <ScrollReveal key={i}>
                <div className="rounded-xl border border-stone bg-white p-6 text-center">
                  <p className="font-display text-4xl font-bold text-charcoal">
                    {stat.value}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-red">
                    {stat.label}
                  </p>
                  <p className="mt-3 text-xs leading-relaxed text-slate">
                    {stat.detail}
                  </p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Deep dive: Retargeting vs Cold */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The fundamentals
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Two audience types, explained
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {/* Retargeting */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border-2 border-red/20 bg-red/5 p-6">
                <div className="mb-4 flex items-center gap-2">
                  <AudienceBadge name="Retargeting" variant="retargeting" />
                  <span className="rounded-full bg-red/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-red">
                    Higher conversion
                  </span>
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Bringing warm prospects back
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Retargeting shows your ads to people who have already visited
                  your website, viewed a listing page, watched one of your
                  videos, or engaged with your social posts. It relies on tracking tools such as the <a href="https://www.facebook.com/business/tools/meta-pixel" target="_blank" rel="noopener noreferrer">Meta Pixel</a> to build custom audiences. These are warm
                  prospects who know your agency exists. They browsed a
                  three-bed in Clapham last Tuesday but did not enquire.
                  Retargeting puts that listing back in front of them while
                  they scroll through Instagram or Facebook.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Much higher conversion rates (5-12%)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Lower cost per enquiry (£2-8)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Keeps your listings top of mind
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Excellent for driving viewings and valuations
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Requires Meta Pixel and sufficient website traffic
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Smaller audience pool (limited to past visitors)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Can feel intrusive if frequency is too high
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>

            {/* Cold Audience */}
            <ScrollReveal>
              <div className="flex h-full flex-col rounded-xl border border-stone bg-white p-6">
                <div className="mb-4">
                  <AudienceBadge name="Cold Audience" variant="cold" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Reaching new prospects at scale
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-slate">
                  Cold audience ads target people who have never interacted
                  with your agency. You define the audience using location,
                  demographics, interests, and behaviours across platforms like Facebook, <a href="https://ads.tiktok.com" target="_blank" rel="noopener noreferrer">TikTok Ads</a>, and YouTube. Think: homeowners
                  aged 30 to 55 within 10 miles of your branch who are
                  interested in property and home improvement. These campaigns
                  cast a wide net to build awareness and fill the top of your
                  marketing funnel with fresh prospects.
                </p>
                <div className="mt-4 space-y-2 text-sm">
                  <p className="font-medium text-charcoal">Strengths:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Massive reach (hundreds of thousands)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      No pixel or prior data required
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Builds brand awareness in your area
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-red" />
                      Feeds your retargeting audiences over time
                    </li>
                  </ul>
                  <p className="mt-3 font-medium text-charcoal">Limitations:</p>
                  <ul className="space-y-1.5 text-slate">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Lower conversion rates (1-3%)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Higher cost per enquiry (£5-15)
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 size-1.5 shrink-0 rounded-full bg-stone" />
                      Longer time from ad view to enquiry
                    </li>
                  </ul>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Performance metrics comparison */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Performance
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              How the numbers compare
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              Based on typical results from UK estate agent video ad campaigns
              across Facebook and Instagram. Your results will vary depending on
              location, listing type, and creative quality. For a platform-by-platform breakdown, see our <Link href="/guides/instagram-vs-facebook-vs-tiktok-property-ads">Instagram vs Facebook vs TikTok guide</Link>.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable
                  rows={PERFORMANCE_DATA}
                  title="Performance metric"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Strategy & setup comparison */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Strategy
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Setup and strategy differences
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              The two approaches require different setups, budgets, and
              creative strategies. Here is what to expect when planning each. Our <Link href="/guides/postcode-targeting-vs-behavioural-targeting-property-ads">postcode vs behavioural targeting guide</Link> covers the geographic side in more detail.
            </p>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <ComparisonTable
                  rows={STRATEGY_DATA}
                  title="Strategy & setup"
                />
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Funnel strategy */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The full picture
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              How retargeting and cold ads work together
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-center text-base text-slate">
              The best-performing estate agents do not choose one or the other.
              They run both strategies as a coordinated funnel that moves
              prospects from awareness to enquiry.
            </p>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-charcoal text-sm font-bold text-white">
                    1
                  </span>
                  <h3 className="font-display text-lg font-bold text-charcoal">
                    Top of funnel: cold awareness
                    <span className="text-red">.</span>
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Run cold audience video ads to homeowners within your target
                  area. Use 20 to 45 second <Link href="/guides/property-video-marketing">property listing videos</Link> that showcase your
                  best properties and position your agency as the local expert.
                  Optimise for video views or landing page visits. This drives
                  traffic to your website and builds your pixel audience. Budget
                  60-70% of your total ad spend here.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-charcoal text-sm font-bold text-white">
                    2
                  </span>
                  <h3 className="font-display text-lg font-bold text-charcoal">
                    Middle of funnel: engagement retargeting
                    <span className="text-red">.</span>
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Retarget people who watched 50% or more of your cold audience
                  videos, or who visited your website but did not enquire. Show
                  them shorter, more specific videos highlighting individual
                  listings they viewed, upcoming open days, or recent price
                  changes. This keeps your agency visible during their decision
                  period.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border-2 border-red/20 bg-red/5 p-6 md:p-8">
                <div className="flex items-center gap-3">
                  <span className="flex size-8 items-center justify-center rounded-full bg-red text-sm font-bold text-white">
                    3
                  </span>
                  <h3 className="font-display text-lg font-bold text-charcoal">
                    Bottom of funnel: conversion retargeting
                    <span className="text-red">.</span>
                  </h3>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Target people who visited specific listing pages, started a
                  valuation form, or spent significant time on your site. This stage delivers the lowest <Link href="/guides/cost-per-lead-video-ads-vs-traditional-property-marketing">cost per lead</Link>. Use
                  10 to 15 second videos with a direct call to action: &ldquo;Book
                  a viewing,&rdquo; &ldquo;Request a valuation,&rdquo; or
                  &ldquo;Still looking in Wandsworth?&rdquo; This is where the
                  highest conversion rates and lowest costs per enquiry come
                  from. Budget 30-40% of your total spend here.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Practical examples */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              In practice
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Real examples for estate agents
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 space-y-6">
            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="mb-3">
                  <AudienceBadge name="Retargeting" variant="retargeting" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Retarget listing page visitors with the same property
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Someone views your three-bed semi in Bristol but leaves without
                  enquiring. Two days later, they see a 15-second video of that
                  exact property in their Instagram feed, with the
                  caption &ldquo;Still thinking about Redland? Book a viewing
                  this weekend.&rdquo; This is the simplest and most effective
                  retargeting setup. Create a custom audience of listing page
                  visitors (last 14 days) and serve them a short, punchy video
                  of the property they already showed interest in. <Link href="/sign-up">Get started with QuiteRight</Link> to create these short retargeting videos from any listing.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="mb-3">
                  <AudienceBadge name="Retargeting" variant="retargeting" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Retarget valuation page visitors for market appraisals
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Visitors to your &ldquo;Free valuation&rdquo; page are
                  considering selling. If they did not complete the form,
                  retarget them with a video showcasing recent sold prices in
                  their area, ending with a clear call to action for a free
                  market appraisal. This audience is small but extremely
                  high-intent. Expect conversion rates of 10-15% and a cost
                  per valuation booking of £5-12.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="mb-3">
                  <AudienceBadge name="Cold Audience" variant="cold" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Target likely movers in your area
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Create a cold audience of homeowners aged 30 to 60 within a
                  10-mile radius of your branch. Layer on interests such as
                  Rightmove, Zoopla, home improvement, and interior design.
                  Serve them a 30-second video showcasing your best current
                  listing or a &ldquo;Just sold&rdquo; success story. The goal
                  is not immediate enquiries; it is getting them to your website
                  so you can retarget them later.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal>
              <div className="rounded-xl border border-stone bg-white p-6 md:p-8">
                <div className="mb-3">
                  <AudienceBadge name="Cold Audience" variant="cold" />
                </div>
                <h3 className="font-display text-lg font-bold text-charcoal">
                  Lookalike audiences from your best clients
                  <span className="text-red">.</span>
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-slate">
                  Upload your past vendor and buyer email lists to Meta and
                  create a 1% lookalike audience. This tells Facebook to find
                  people who share similar characteristics with your existing
                  clients. Lookalike audiences typically outperform pure
                  interest-based targeting by 20-40% on cost per enquiry. Pair
                  this with a compelling listing video and you have a powerful
                  cold campaign.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* QuiteRight callout */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <div className="rounded-xl border-2 border-red/20 bg-red/5 p-8 md:p-10">
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                Your creative sorted
              </p>
              <h2 className="mt-4 font-display text-2xl font-bold text-charcoal md:text-3xl">
                Create retargeting and cold audience video ads in 60 seconds
                <span className="text-red">.</span>
              </h2>
              <p className="mt-4 text-base leading-relaxed text-slate">
                The hardest part of running a full-funnel ad strategy is
                producing different creative for each audience. QuiteRight
                removes that barrier. Paste a listing URL, choose your format,
                and get professional video ads ready for both retargeting and
                cold campaigns. No filming, no editing, no agency fees. See <Link href="/how-it-works">how it works</Link>.
              </p>
              <div className="mt-6 grid gap-4 sm:grid-cols-2">
                {[
                  {
                    title: "Cold audience creative",
                    detail:
                      "Full property showcase videos (20-45s) designed to stop the scroll and drive website visits from new prospects.",
                  },
                  {
                    title: "Retargeting creative",
                    detail:
                      "Short, punchy reminder videos (10-20s) with direct calls to action for people who already know your listing.",
                  },
                ].map((item) => (
                  <div
                    key={item.title}
                    className="rounded-lg bg-white p-4 text-center"
                  >
                    <p className="text-sm font-semibold text-charcoal">
                      {item.title}
                    </p>
                    <p className="mt-1 text-xs text-slate">{item.detail}</p>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/how-it-works"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  See how it works
                </Link>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <FaqAccordion items={FAQ} />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Related guides */}
      <section className="border-t border-stone bg-cream px-6 py-16">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <h2 className="font-display text-2xl font-bold text-charcoal">
              Related guides
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>
          <div className="mt-6 grid gap-4 sm:grid-cols-3">
            {[
              {
                href: "/guides/instagram-vs-facebook-vs-tiktok-property-ads",
                title: "Instagram vs Facebook vs TikTok for property ads",
                desc: "Compare the three major platforms for property video advertising side by side.",
              },
              {
                href: "/guides/property-video-marketing",
                title: "Property video marketing guide",
                desc: "Everything you need to know about using video to market listings in 2026.",
              },
              {
                href: "/guides/social-media-estate-agents",
                title: "Social media for estate agents",
                desc: "A complete guide to building your agency's social media presence.",
              },
            ].map((item) => (
              <Link
                key={item.href}
                href={item.href}
                className="group flex flex-col rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/30 hover:shadow-sm"
              >
                <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                  {item.title}
                </p>
                <p className="mt-1 flex-1 text-sm text-slate">{item.desc}</p>
                <p className="mt-3 flex items-center gap-1 text-xs font-medium text-red">
                  Read guide
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-3.5 transition-transform group-hover:translate-x-0.5"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5 10a.75.75 0 0 1 .75-.75h6.638L10.23 7.29a.75.75 0 1 1 1.04-1.08l3.5 3.25a.75.75 0 0 1 0 1.08l-3.5 3.25a.75.75 0 1 1-1.04-1.08l2.158-1.96H5.75A.75.75 0 0 1 5 10Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
              Every listing deserves a full-funnel strategy
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
              QuiteRight creates professional property videos for retargeting
              and cold audience campaigns in under 60 seconds. More views,
              more enquiries, more instructions. Also read our guide on <Link href="/guides/paid-video-ads-vs-organic-social-estate-agents">paid vs organic social</Link> to round out your strategy.
            </p>
            <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-lg bg-red px-7 py-3.5 text-base font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-lg border border-cream/20 px-7 py-3.5 text-base font-medium text-cream transition-colors hover:border-cream/40 hover:bg-cream/5 sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
            <p className="mt-5 text-sm text-warm-grey">
              14-day free trial &middot; No credit card &middot; Cancel any time
            </p>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
